<template>
  <div class="day">
    <div v-if="$mq === 'mobile'"
         class="day-title">{{ $t(`weekday.${day.title}`) | weekDayShort }}</div>
    <div v-else
         class="day-title">{{ $t(`weekday.${day.title}`) }}</div>
    <div class="day-body"
         ref="dayBody"
         :style="dayBodyStyles">

      <time-block ref="timeBlocks"
                  v-for="(timeBlock, index) in timeBlocks"

                  :key="index + timeBlock.mode_name"
                  :mode-name="timeBlock.mode_name"
                  :height="timeBlock.height"
                  :top="timeBlock.top"
                  :min-block-height="minBlockHeight"
                  :min-block-time="minBlockTime"
                  :parent-bottom="dayBodyHeight"

                  @resizeStart="onResizeStart(timeBlock)"
                  @resizeEnd="onResizeEnd(index)"
                  @resize="onTimeBlockResize(index, $event)"
                  @moveStart="setSelectedTimeBlock(timeBlock)"
                  @move="onTimeBlockMove(index, $event)"
                  @moveEnd="onTimeBlockMoveEnd(index, $event)"
                  @delete="removeTimeBlock(timeBlock)"/>

      <time-block ref="standardBlocks"
                  v-for="(block, index) in standardBlocks"

                  :key="index + block.mode_name"
                  :mode-name="block.mode_name"
                  :height="block.height"
                  :top="block.top"
                  :min-block-height="minBlockHeight"
                  :min-block-time="minBlockTime"
                  @click="onStandardBlockClick($event)"/>

      <mode-picker ref="modePicker"
                   :child-id="childId"
                   @select="onModeSelect"
                   @clickOutside="onModePickerClickOutside"
                   @close="closeModePicker" />

      <div class="hour-divisor"
           v-for="coordinate in hourDivisorCoordinates"
           :key="coordinate"
           :style="{ top: `${coordinate}px` }">
      </div>

      <div class="current-time"
           v-if="showCurrentTime"
           :style="{ top: `${currentTimeInMinutes  * minToPix}px` }">
        {{ currentDate | localDateTime }} {{ $t("now") }}
      </div>
    </div>
  </div>
</template>

<script>
  import TimeBlock from './ScheduleTimeBlock.vue'
  import ModePicker from './ScheduleModePicker.vue'

  import { convertMinsToHrsMins } from '@/services/utils.js'
  import { getDayOfTheWeek } from '@/services/intl'

  import analytics from '@/services/analytics.js'

  export default {
    props: {
      day: {
        required: true,
        type: Object
      },
      childId: {
        required: true
      },
      minBlockHeight: {
        required: true,
        type: Number
      },
      minBlockTime: {
        required: true,
        type: Number
      },
      currentDate: {
        required: true,
        type: Date
      }
    },

    components: {
      TimeBlock,
      ModePicker
    },

    data() {
      return {
        dayBodyHeight: null,
        timeBlocks: [],
        standardBlocks: [],
        resizing: false,
        modePickerTop: null,
        selectedTimeBlock: null,
        timeBlockAboveBeforeChange: null,
        timeBlockBelowBeforeChange: null,
        hourDivisorCoordinates: []
      }
    },

    mounted() {
      this.setDayBodyHeight()
      this.setHourDivisorCoordinates()
    },

    methods: {
      onStandardBlockClick(event) {
        this.showModePicker(event.clientY)
      },

      onModePickerClickOutside(event) {
        this.closeModePicker()

        if (!this.$refs.standardBlocks) {
          return
        }

        let targets = this.$refs.standardBlocks.map(timeBlock => timeBlock.$el)
        let target = event.target

        while (target !== document.documentElement) {
          if (targets.includes(target)) {
            this.showModePicker(event.clientY)

            return
          }

          target = target.parentNode
        }
      },

      closeModePicker() {
        this.$refs.modePicker.hide()
      },

      /**
       * Show mode picker.
       */
      showModePicker(clientY) {
        this.modePickerTop = this.convertToBodyTop(clientY)
        this.$refs.modePicker.show(this.modePickerTop, this.dayBodyHeight)
      },

      /**
       * Create new block when the mode is selected.
       */
      onModeSelect({ mode_id, mode_name }) {
        let closestTop = Math.floor(this.modePickerTop / this.minBlockHeight) * this.minBlockHeight
        let timeBlock = this.getTimeBlockAttributes(closestTop, mode_id, mode_name)
        let attributes = _.omit(timeBlock, ['top', 'height'])
        this.closeModePicker()

        this.timeBlocks.push(attributes)
        this.sortTimeBlocksByTopCoordinate()
        this.setStandardBlocks()

        this.$emit('updateTimeBlocks', {
          day: this.day,
          timeBlocks: this.timeBlocks
        })
      },

      showSuccessNotification() {
        this.$notify({
          type: 'success',
          text: 'Schedule successfully updated'
        })
      },

      showFailNotification() {
        this.$notify({
          type: 'error',
          text: 'Failed to update Schedule'
        })
      },

      /**
       * Create new block.
       */
      getTimeBlockAttributes(top, mode_id, mode_name) {
        let height = this.getMaxPossibleTimeBlockHeight(top)
        let duration = height * this.pixToMin
        let minutes = top * this.pixToMin

        let daysEmpty = '0000000'
        let days = daysEmpty.substring(0, this.day.dayIndex) + '1' + daysEmpty.substring(this.day.dayIndex + 1)

        return {
          top,
          height: height,
          mode_name: mode_name,

          name: 'Sample Name',
          days,
          start_time: convertMinsToHrsMins(minutes) + ':00',
          duration_in_minutes: duration,
          is_repeating: true,
          is_default: false,
          user_id: this.childId,
          mode_id
        }
      },

      /**
       * Get max possible height for a block by it's top coordinate
       *
       * @param top
       *
       * @returns Number
       */
      getMaxPossibleTimeBlockHeight(top) {
        let nextClosestTimeBlock = this.timeBlocks.find(timeBlock => timeBlock.top > top)
        let freeSpace

        freeSpace = nextClosestTimeBlock ? nextClosestTimeBlock.top - top : this.dayBodyHeight - top

        return freeSpace < this.hourHeight ? freeSpace : this.hourHeight
      },

      onTimeBlockResize(index, event) {
        analytics.sendEvent('St_Schedule')

        let timeBlock = this.timeBlocks[index]

        let newTop = timeBlock.top + event.deltaRect.top
        let newHeight = timeBlock.height + event.deltaRect.height
        let newBottom = newTop + newHeight

        let timeBlockAbove = this.timeBlocks[index - 1]

        if (timeBlockAbove) {
          let timeBlockAboveBottom = this.getBlockBottom(timeBlockAbove)

          if (timeBlockAboveBottom > newTop) {
            if (timeBlockAbove.height > this.minBlockHeight) {
              timeBlockAbove.height = Math.max(newTop - timeBlockAbove.top, this.minBlockHeight)
            } else {
              newTop = timeBlockAboveBottom
            }
          } else if (timeBlockAboveBottom < newTop && newHeight >= event.rect.height) {
            timeBlockAbove.height = Math.min(newTop - timeBlockAbove.top, this.timeBlockAboveBeforeChange.height)
          }
        }

        let timeBlockBelow = this.timeBlocks[index + 1]

        if (timeBlockBelow) {
          let timeBlockBelowBottom = this.getBlockBottom(timeBlockBelow)

          if (newBottom > timeBlockBelow.top) {
            if (timeBlockBelow.height > this.minBlockHeight) {
              timeBlockBelow.height = Math.max(timeBlockBelowBottom - newBottom, this.minBlockHeight)
              timeBlockBelow.top = timeBlockBelowBottom - timeBlockBelow.height
            }

            newBottom = timeBlockBelow.top
          } else if (newBottom < timeBlockBelow.top && newHeight >= event.rect.height) {
            timeBlockBelow.top = Math.max(newBottom, this.timeBlockBelowBeforeChange.top)
            timeBlockBelow.height = timeBlockBelowBottom - timeBlockBelow.top
          }
        }

        if (newHeight < event.rect.height) {
          // Cursor is not on the block
          return
        }

        timeBlock.top = newTop
        timeBlock.height = newBottom - newTop
        timeBlock.start_time = convertMinsToHrsMins(timeBlock.top * this.pixToMin) + ':00'
        timeBlock.duration_in_minutes = timeBlock.height * this.pixToMin

        this.setStandardBlocks()
      },

      /**
       * Handle dragging the timeblock.
       *
       * @param index
       * @param event
       */
      onTimeBlockMove(index, event) {
        analytics.sendEvent('St_Schedule')

        let timeBlock = this.timeBlocks[index]
        let timeBlockAbove = this.timeBlocks[index - 1]
        let timeBlockBelow = this.timeBlocks[index + 1]
        let timeBlockOffset = event.dy
        let cursorPosition = event.clientY - timeBlockOffset
        let timeBlockPosition = this.$refs.timeBlocks[index].$el.getBoundingClientRect()
        let isCursorOutsideBlock = cursorPosition < timeBlockPosition.top || cursorPosition > timeBlockPosition.bottom

        if (isCursorOutsideBlock) {
          return
        }

        let newTop = timeBlock.top + timeBlockOffset
        let newBottom = newTop + timeBlock.height
        let isRestrictedByAboveBlock = timeBlockAbove && newTop < this.getBlockBottom(timeBlockAbove)
        let isRestrictedByBelowBlock = timeBlockBelow && newBottom > timeBlockBelow.top

        if (isRestrictedByAboveBlock || isRestrictedByBelowBlock) {
          return
        }

        let isRestrictedByParentTop = newTop < 0
        let isRestrictedByParentBottom = newBottom > this.dayBodyHeight

        if (isRestrictedByParentTop || isRestrictedByParentBottom) {
          return
        }

        timeBlock.top = newTop
        timeBlock.start_time = convertMinsToHrsMins(timeBlock.top * this.pixToMin) + ':00'
        timeBlock.duration_in_minutes = timeBlock.height * this.pixToMin

        this.setStandardBlocks()
      },

      onTimeBlockMoveEnd(index) {
        let timeBlock = this.timeBlocks[index]

        if (!this.isTimeBlockModified(timeBlock, this.selectedTimeBlockBeforeChange)) {
          this.selectedTimeBlockBeforeChange = null

          return
        }

        this.mergeWithSiblingBlocks(timeBlock)

        this.$emit('updateTimeBlocks', {
          day: this.day,
          timeBlocks: this.timeBlocks
        })
      },

      mergeWithSiblingBlocks(timeBlock) {
        let timeBlockIndex = this.timeBlocks.indexOf(timeBlock)
        let previousTimeBlock = this.timeBlocks[timeBlockIndex - 1]
        let nextTimeBlock = this.timeBlocks[timeBlockIndex + 1]
        let shouldMergeWithPreviousTimeBlock = previousTimeBlock && this.shouldMergeWithPreviousTimeBlock(timeBlockIndex)
        let shouldMergeWithNextTimeBlock = nextTimeBlock && this.shouldMergeWithNextTimeBlock(timeBlockIndex)

        if (shouldMergeWithPreviousTimeBlock) {
          timeBlock.top = previousTimeBlock.top
          timeBlock.height = timeBlock.height + previousTimeBlock.height

          this.removeTimeBlock(previousTimeBlock)
        }

        if (shouldMergeWithNextTimeBlock) {
          timeBlock.height = timeBlock.height + nextTimeBlock.height

          this.removeTimeBlock(nextTimeBlock)
        }

        if (shouldMergeWithPreviousTimeBlock || shouldMergeWithNextTimeBlock) {
          timeBlock.start_time = convertMinsToHrsMins(timeBlock.top * this.pixToMin) + ':00'
          timeBlock.duration_in_minutes = timeBlock.height * this.pixToMin
        }
      },

      shouldMergeWithPreviousTimeBlock(timeBlockIndex) {
        let previousTimeBlock = this.timeBlocks[timeBlockIndex - 1]

        if (!previousTimeBlock) {
          return false
        }

        let timeBlock = this.timeBlocks[timeBlockIndex]
        let previousTimeBlockBottom = this.getBlockBottom(previousTimeBlock)

        return timeBlock.top === previousTimeBlockBottom && timeBlock.mode_name === previousTimeBlock.mode_name
      },

      shouldMergeWithNextTimeBlock(timeBlockIndex) {
        let nextTimeBlock = this.timeBlocks[timeBlockIndex + 1]

        if (!nextTimeBlock) {
          return false
        }

        let timeBlock = this.timeBlocks[timeBlockIndex]
        let timeBlockBottom = this.getBlockBottom(timeBlock)

        return timeBlockBottom === nextTimeBlock.top && timeBlock.mode_name === nextTimeBlock.mode_name
      },

      /**
       * On resize start.
       */
      onResizeStart(timeBlock) {
        analytics.sendEvent('St_Schedule')

        this.resizing = true

        this.setSelectedTimeBlock(timeBlock)

        let selectedTimeBlockIndex = this.timeBlocks.indexOf(timeBlock)

        this.timeBlockAboveBeforeChange = this.getClonedTimeBlock(this.timeBlocks[selectedTimeBlockIndex - 1])
        this.timeBlockBelowBeforeChange = this.getClonedTimeBlock(this.timeBlocks[selectedTimeBlockIndex + 1])
      },

      /**
       * Save the block's initial state for future use
       *
       * @param timeBlock
       *
       */
      setSelectedTimeBlock(timeBlock) {
        analytics.sendEvent('St_Schedule')

        this.selectedTimeBlockBeforeChange = this.getClonedTimeBlock(timeBlock)
      },

      getClonedTimeBlock(timeBlock) {
        return timeBlock ? _.cloneDeep(timeBlock) : null
      },

      onResizeEnd(index) {
        setTimeout(() => {
          this.resizing = false
        }, 100)

        let timeBlock = this.timeBlocks[index]
        this.mergeWithSiblingBlocks(timeBlock)

        let previousTimeBlock = this.timeBlocks[index - 1]
        let nextTimeBlock = this.timeBlocks[index + 1]

        if (previousTimeBlock && this.isTimeBlockModified(previousTimeBlock, this.timeBlockAboveBeforeChange)) {
          previousTimeBlock.start_time = convertMinsToHrsMins(timeBlock.top * this.pixToMin) + ':00'
          previousTimeBlock.duration_in_minutes = timeBlock.height * this.pixToMin
        }

        if (nextTimeBlock && this.isTimeBlockModified(nextTimeBlock, this.timeBlockBelowBeforeChange)) {
          nextTimeBlock.start_time = convertMinsToHrsMins(timeBlock.top * this.pixToMin) + ':00'
          nextTimeBlock.duration_in_minutes = timeBlock.height * this.pixToMin
        }

        if (this.isTimeBlockModified(timeBlock, this.selectedTimeBlockBeforeChange)) {
          timeBlock.start_time = convertMinsToHrsMins(timeBlock.top * this.pixToMin) + ':00'
          timeBlock.duration_in_minutes = timeBlock.height * this.pixToMin
        }

        this.selectedTimeBlockBeforeChange = null
        this.timeBlockAboveBeforeChange = null
        this.timeBlockBelowBeforeChange = null

        this.$emit('updateTimeBlocks', {
          day: this.day,
          timeBlocks: this.timeBlocks
        })
      },

      /**
       * Check if the block has been somehow changed in comparison to it's initial state (this.selectedTimeBlockBeforeChange)
       *
       * @param timeBlock
       *
       * @returns Boolean
       */
      isTimeBlockModified(timeBlock, timeBlockBeforeChange) {
        return Object.keys(timeBlock).some(key => timeBlock[key] !== timeBlockBeforeChange[key])
      },

      /**
       * Calculate bottom of the timeblock.
       *
       * @param timeBlock
       */
      getBlockBottom(timeBlock) {
        return timeBlock.top + timeBlock.height
      },

      /**
       * Remove the timeblock.
       *
       * @param timeBlock
       */
      removeTimeBlock(timeBlock) {
        analytics.sendEvent('St_Schedule')

        let timeBlockIndex = this.timeBlocks.findIndex(tb => tb.id === timeBlock.id)

        this.timeBlocks.splice(timeBlockIndex, 1)
        this.setStandardBlocks()

        this.$emit('updateTimeBlocks', {
          day: this.day,
          timeBlocks: this.timeBlocks
        })
      },

      /**
       * Calculate & append top and height to the timeblock.
       *
       * @param timeBlock
       */
      importTimeblock(timeBlock) {
        let startTimeParts = timeBlock.start_time.split(':')
        let minutes = parseInt(startTimeParts[0]) * 60 + parseInt(startTimeParts[1])

        let top = minutes * this.minToPix
        let height = timeBlock.duration_in_minutes * this.minToPix

        return {
          ...timeBlock,
          ...{
            top,
            height
          }
        }
      },

      /**
       * Sort time blocks by top coordinate.
       *
       * When user adds a timeblock between two others it will be
       * pushed to the end of array, while it should be
       * placed between two other blocks.
       */
      sortTimeBlocksByTopCoordinate() {
        this.timeBlocks.sort((a, b) => a.top - b.top)
      },

      convertToBodyTop(top) {
        let rect = this.$refs.dayBody.getBoundingClientRect()

        return top - rect.top
      },

      setStandardBlocks() {
        this.standardBlocks = this.getTimeGaps().map(gap => {
          return {
            top: gap.top,
            height: gap.bottom - gap.top,
            mode_name: 'Standard'
          }
        })
      },

      getTimeGaps() {
        let gaps = []

        this.timeBlocks.forEach((timeBlock, index) => {
          let timeBlockBottom = this.getBlockBottom(timeBlock)

          if (index === 0 && timeBlock.top !== 0) {
            gaps.push({
              top: 0,
              bottom: timeBlock.top
            })
          }

          if (index === this.timeBlocks.length - 1) {
            if (timeBlockBottom === this.dayBodyHeight) {
              return
            }

            gaps.push({
              top: timeBlockBottom,
              bottom: this.dayBodyHeight
            })

            return
          }

          let nextTimeBlock = this.timeBlocks[index + 1]

          if (timeBlockBottom === nextTimeBlock.top) {
            return
          }

          gaps.push({
            top: timeBlockBottom,
            bottom: nextTimeBlock.top
          })
        })

        if (!this.timeBlocks.length) {
          gaps.push({
            top: 0,
            bottom: this.dayBodyHeight
          })
        }

        return gaps
      },

      setDayBodyHeight() {
        this.dayBodyHeight = Math.round(this.$refs.dayBody.getBoundingClientRect().height)
      },

      setHourDivisorCoordinates() {
        this.hourDivisorCoordinates = []

        for (let i = 0; i < this.dayBodyHeight; i += this.hourHeight) {
          this.hourDivisorCoordinates.push(i)
        }
      }
    },

    computed: {
      minToPix() {
        return this.minBlockHeight / this.minBlockTime
      },

      pixToMin() {
        return this.minBlockTime / this.minBlockHeight
      },

      hourHeight() {
        return 60 * this.minToPix
      },

      dayBodyStyles() {
        let minutesInDay = 60 * 24
        let height = minutesInDay * this.minToPix

        return [
          { height: height + 'px' }
        ]
      },

      showCurrentTime() {
        return getDayOfTheWeek(this.currentDate) === this.day.dayIndex
      },

      currentTimeInMinutes() {
        return (new Date(this.currentDate).getHours() * 60) + new Date(this.currentDate).getMinutes()
      }
    },

    watch: {
      day: {
        handler: function(day) {
          this.timeBlocks = day.timeBlocks.map(this.importTimeblock)
          this.sortTimeBlocksByTopCoordinate()

          this.setStandardBlocks()
        },
        deep: true
      }
    }
  }
</script>

<style lang="scss" scoped>
  .day {
    display: flex;
    flex: 1 0;
    flex-direction: column;
    justify-content: center;
    margin-right: 1px;
  }

  .day-title {
    display: flex;
    flex: 0 0 40px;
    align-items: center;
    justify-content: center;
    padding-bottom: 15px;
  }

  .day-body {
    display: flex;
    flex-direction: column;
    flex: 1 0;
    position: relative;
    background-color: rgba(#787196, .15);
  }

  .hour-divisor {
    position: absolute;
    left: 0;
    right: 0;
    height: 1px;
    background-color: rgba(#b9b9b9, .2);
  }

  .current-time {
    position: absolute;
    right: 0;
    z-index: 5;
    width: 100%;
    padding: 2px 5px;
    font-size: 9px;
    font-weight: bold;
    color: #3e3a3b;
    text-align: right;
    opacity: .75;
    transform: translateY(-100%);
    pointer-events: none;
    user-select: none;

    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      right: 0;
      display: block;
      width: 50%;
      height: 1px;
      background-image: linear-gradient(to right, rgba(#000, 0), currentColor);
    }
  }
</style>
